import { api } from '../../core/network/api';

/**
 * What a delver walks in with. Served rather than mirrored — `BASE_WEIGHT_KG`,
 * `LOOT.CARRY_CAPACITY_KG` and a character's own `carryCapacityBonusKg` all
 * live on the server, and the last is talent state this client has never been
 * told. See `backend/src/game/loadout/sim/loadout.ts`.
 */

export interface LoadoutGear {
  weaponClass: 'LIGHT' | 'MEDIUM' | 'BLUNT' | 'HEAVY' | 'REACH';
  /** design/gdd/equipment.md — what is actually equipped, not what the class defaults to. */
  weaponId: string;
  weaponName: string;
  weaponModel: string;
  offhandId: string | null;
  offhandName: string | null;
  offhandModel: string | null;
  weightKg: number;
  damage: number;
  swingMs: number;
  reachM: number;
  armorClass: 'CLOTH' | 'LEATHER' | 'MAIL' | 'PLATE';
  physicalReduction: number;
  magicResistance: number;
  moveSpeedMultiplier: number;
  /** equipment.md's worn-gear amendment — what is in the four gear slots, keyed by slot. */
  worn?: Partial<Record<string, { id: string; name: string; tier: number; line: string }>>;
  /** What the worn set weighs. Already inside `weightKg`; surfaced so the strip can attribute it. */
  gearWeightKg?: number;
  poise?: number;
  /**
   * Passive dodge chance, unladen (2026-10-16): class base + worn gear +
   * talents, clamped. A full pack halves it in the run. Optional: an older
   * server does not send it.
   */
  dodgeChance?: number;
}

export interface LoadoutSatchelSlot {
  defId: string;
  name: string;
  slot: number;
  charges: number;
  weightKg: number;
}

export interface LoadoutRow {
  characterId: string;
  gear: LoadoutGear;
  satchel: LoadoutSatchelSlot[];
  startWeightKg: number;
  capacityKg: number;
}

export const getLoadout = (characterId: string): Promise<LoadoutRow> =>
  api.get(`/loadout/${encodeURIComponent(characterId)}`);
